import React, { Suspense, lazy, useCallback, useEffect, useMemo, useState } from "react";
import { useInRouterContext, useSearchParams } from "react-router-dom";
import Dialog from "@mui/material/Dialog";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import CloseIcon from "@mui/icons-material/Close";
import { practiceDepartments, practiceFeatures } from "../../config/practice";
import SideNavLayout from "../../customsidenav/sidenavlayout";
import { clearConfigCache, formwizApi } from "./formwizApi";
import FormPreviewDialog from "./FormPreviewDialog";
import { editorApi } from "../editor/editorApi";
import { LEGAL_RECORD_CATEGORIES } from "./emrPdf";
import {
  CATEGORY_LABEL,
  CHANNELS,
  CHANNEL_SHORT,
  baseUrlError,
  displayNameError,
  errInfo,
  moveItem,
  titleOf,
} from "./staffUtils";
import "./staff.css";

// The form editor (admins) is its own lazy chunk; this page only gains the
// buttons, the badges and the ?edit=<form_id> host.
const FormEditor = lazy(() => import("../editor/FormEditor"));
const NewFormDialog = lazy(() => import("../editor/NewFormDialog"));
const DuplicateDialog = lazy(() => import("../editor/DuplicateDialog"));

// ?edit=<form_id> opens the editor over the page. Inside care's router it
// uses the router's search params; rendered alone (tests) it uses the
// window location directly.
function RouterEditParam({ children }) {
  const [params, setParams] = useSearchParams();
  const set = useCallback((id) => {
    setParams((p) => {
      const n = new URLSearchParams(p);
      if (id) n.set("edit", id);
      else n.delete("edit");
      return n;
    }, { replace: !id });
  }, [setParams]);
  return children(params.get("edit") || null, set);
}

function WindowEditParam({ children }) {
  const read = () => (typeof window !== "undefined" ? new URLSearchParams(window.location.search).get("edit") : null);
  const [id, setId] = useState(read);
  useEffect(() => {
    const on = () => setId(read());
    window.addEventListener("popstate", on);
    return () => window.removeEventListener("popstate", on);
  }, []);
  const set = useCallback((next) => {
    try {
      const u = new URL(window.location.href);
      if (next) u.searchParams.set("edit", next);
      else u.searchParams.delete("edit");
      if (next) window.history.pushState(window.history.state, "", u.toString());
      else window.history.replaceState(window.history.state, "", u.toString());
    } catch (_) { /* no history API */ }
    setId(next || null);
  }, []);
  return children(id, set);
}

export function EditParamHost({ children }) {
  const inRouter = useInRouterContext();
  return inRouter ? <RouterEditParam>{children}</RouterEditParam> : <WindowEditParam>{children}</WindowEditParam>;
}

export const ADMIN_SUBCLASSES = ["ADMIN_CONSENT", "ADMIN_HIPAA", "ADMIN_SIGNEDFORMSLETTERS", "ADMIN_MISC"];
const CLINICAL = { endpoint: "clinicaldocument", documentsubclass: "CLINICALDOCUMENT" };
const targetKey = (t) => (t ? `${t.endpoint}:${t.documentsubclass}` : "clinicaldocument:CLINICALDOCUMENT");

// EMR targets an admin may pick. Admin documents are offered only when the
// server lists them: an explicit `emr_targets` list in GET /config, or a form
// whose effective target already is an admin document (FORMWIZ_ATHENA_ADMIN_DOCS
// on). Otherwise clinical document only.
export function emrTargetOptions(config) {
  if (config && Array.isArray(config.emr_targets) && config.emr_targets.length) {
    return config.emr_targets.map((t) => ({ endpoint: t.endpoint, documentsubclass: t.documentsubclass, label: t.label || t.documentsubclass }));
  }
  const forms = (config && config.forms) || {};
  const adminOn = Object.values(forms).some((f) => f && f.emr && f.emr.endpoint === "admin");
  const opts = [{ ...CLINICAL, label: "Clinical document" }];
  if (adminOn) ADMIN_SUBCLASSES.forEach((s) => opts.push({ endpoint: "admin", documentsubclass: s, label: `Admin: ${s.replace("ADMIN_", "").toLowerCase()}` }));
  return opts;
}

// Changed top-level keys of the practice settings (emr compared as a whole).
export function changedSettings(original, draft) {
  const out = {};
  Object.keys(draft).forEach((k) => {
    if (k === "version") return;
    if (JSON.stringify(draft[k]) !== JSON.stringify(original ? original[k] : undefined)) out[k] = draft[k];
  });
  return out;
}

function Switch({ checked, onChange, label, disabled, testid }) {
  return (
    <label className="fws-switch">
      <input type="checkbox" checked={!!checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} data-testid={testid} />
      <span className="fws-switch-track" aria-hidden="true" />
      <span>{label}</span>
    </label>
  );
}

// Internal staff forms (their own table, a lazy chunk).
const InternalFormsSettings = React.lazy(() => import("../internal/InternalFormsSettings"));

function PacketEditor({ packet, forms, onSave, onCancel, busy }) {
  const [name, setName] = useState(packet ? packet.name : "");
  const [description, setDescription] = useState(packet ? packet.description || "" : "");
  const [lang, setLang] = useState(packet && packet.default_language ? packet.default_language : "");
  const [ids, setIds] = useState(packet ? [...(packet.form_ids || [])] : []);
  const [sortOrder, setSortOrder] = useState(packet && packet.sort_order !== undefined ? packet.sort_order : 0);
  const [add, setAdd] = useState("");
  const byId = useMemo(() => new Map(forms.map((f) => [f.id, f])), [forms]);
  const valid = name.trim() && ids.length >= 1 && ids.length <= 20;
  return (
    <div className="fws-section fws-stack" data-testid="packet-editor">
      <div className="fws-settings-grid">
        <div className="fws-field">
          <label className="fws-label" htmlFor="fws-pk-name">Packet name</label>
          <input id="fws-pk-name" className="fws-input" maxLength={120} value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="fws-field">
          <label className="fws-label" htmlFor="fws-pk-lang">Default language</label>
          <select id="fws-pk-lang" className="fws-select" value={lang} onChange={(e) => setLang(e.target.value)}>
            <option value="">Practice default</option>
            <option value="en">English</option>
            <option value="es">Español</option>
          </select>
        </div>
        <div className="fws-field">
          <label className="fws-label" htmlFor="fws-pk-sort">Sort order</label>
          <input id="fws-pk-sort" type="number" className="fws-input" value={sortOrder} onChange={(e) => setSortOrder(Number(e.target.value) || 0)} />
        </div>
      </div>
      <div className="fws-field">
        <label className="fws-label" htmlFor="fws-pk-desc">Description</label>
        <input id="fws-pk-desc" className="fws-input" maxLength={500} value={description} onChange={(e) => setDescription(e.target.value)} />
      </div>
      <div>
        <div className="fws-label">Forms, in order</div>
        <ol className="fws-sel-list">
          {ids.map((id, i) => (
            <li key={id} className="fws-sel-item">
              <span className="fws-sel-num">{i + 1}.</span>
              <span className="fws-sel-name">{byId.has(id) ? titleOf(byId.get(id).title) : id}</span>
              <button type="button" className="fws-icon-btn" aria-label="Move up" disabled={i === 0} onClick={() => setIds((s) => moveItem(s, i, -1))}><ArrowUpwardIcon fontSize="small" /></button>
              <button type="button" className="fws-icon-btn" aria-label="Move down" disabled={i === ids.length - 1} onClick={() => setIds((s) => moveItem(s, i, 1))}><ArrowDownwardIcon fontSize="small" /></button>
              <button type="button" className="fws-icon-btn" aria-label="Remove" onClick={() => setIds((s) => s.filter((x) => x !== id))}><CloseIcon fontSize="small" /></button>
            </li>
          ))}
        </ol>
        <div className="fws-row" style={{ marginTop: 6 }}>
          <select className="fws-select" style={{ maxWidth: 360 }} aria-label="Add a form" value={add} onChange={(e) => setAdd(e.target.value)}>
            <option value="">Add a form…</option>
            {forms.filter((f) => !ids.includes(f.id)).map((f) => <option key={f.id} value={f.id}>{titleOf(f.title)}</option>)}
          </select>
          <button type="button" className="fws-btn fws-btn-sm" disabled={!add} onClick={() => { setIds((s) => [...s, add]); setAdd(""); }}>Add</button>
        </div>
      </div>
      <div className="fws-row">
        <button
          type="button"
          className="fws-btn fws-btn-primary"
          disabled={!valid || busy}
          onClick={() => onSave({ name: name.trim(), description: description.trim(), form_ids: ids, default_language: lang || null, sort_order: sortOrder })}
        >
          {busy ? "Saving…" : "Save packet"}
        </button>
        <button type="button" className="fws-btn fws-btn-ghost" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

/*
 * Form settings (/form-settings, admins): practice settings, EMR filing, the
 * forms offered (enable / auto-post / EMR target) and packets.
 */
function FormSettings({ editId = null, setEditId = () => {} }) {
  const [phase, setPhase] = useState("loading"); // loading | ready | unavailable | error
  const [config, setConfig] = useState(null);
  const [draft, setDraft] = useState(null);
  const [forms, setForms] = useState([]);
  const [packets, setPackets] = useState([]);
  const [saving, setSaving] = useState("");
  const [message, setMessage] = useState(null); // {tone, text}
  const [rowBusy, setRowBusy] = useState("");
  const [editing, setEditing] = useState(null); // null | "new" | packet
  const [preview, setPreview] = useState(null);
  // Form editor: "off" (not an admin, or the server has no editor: 404 /
  // network) | "not_installed" (503: editor tables missing) | "ok".
  const [editor, setEditor] = useState({ state: "off", forms: new Map(), list: [], libraryCount: 0 });
  const [newOpen, setNewOpen] = useState(false);
  // Duplicate: {form_id, version} (a row's form) | "library" (pick a standard
  // form, e.g. another practice's instrument, into a locked copy) | null.
  const [dupSource, setDupSource] = useState(null);

  const loadEditor = useCallback(async () => {
    try {
      const r = await editorApi.list();
      const list = Array.isArray(r && r.forms) ? r.forms : [];
      const libraryCount = Number((r && r.library_count) || 0);
      setEditor({ state: "ok", forms: new Map(list.map((f) => [f.form_id, f])), list, libraryCount });
    } catch (e) {
      setEditor({ state: errInfo(e).status === 503 ? "not_installed" : "off", forms: new Map(), list: [], libraryCount: 0 });
    }
  }, []);

  const load = useCallback(async () => {
    try {
      const cfg = await formwizApi.config();
      let cat;
      try {
        cat = await formwizApi.catalog(true);
      } catch (e) {
        if (errInfo(e).status !== 403) throw e;
        cat = await formwizApi.catalog(false);
      }
      let pk = [];
      try {
        pk = await formwizApi.packets(!!(cfg && cfg.admin));
      } catch (_) {
        pk = (cat && cat.packets) || [];
      }
      setConfig(cfg || {});
      setDraft({ ...((cfg && cfg.practice) || {}), emr: { ...(((cfg && cfg.practice) || {}).emr || {}) } });
      setForms(Array.isArray(cat && cat.forms) ? cat.forms : []);
      setPackets(Array.isArray(pk) ? pk : []);
      setPhase("ready");
      // Editor list (admins only): never holds the page up.
      if (cfg && cfg.admin) loadEditor();
    } catch (e) {
      setPhase(errInfo(e).status === 404 ? "unavailable" : "error");
    }
  }, [loadEditor]);

  useEffect(() => {
    load();
  }, [load]);

  const admin = !!(config && config.admin);
  const linkHosts = (config && config.link_hosts) || [];
  const original = (config && config.practice) || {};
  const nameErr = draft ? displayNameError(draft.practice_display_name) : "";
  const urlErr = draft ? baseUrlError(draft.public_base_url, linkHosts) : "";
  const changes = draft ? changedSettings(original, draft) : {};
  const dirty = Object.keys(changes).length > 0;
  const targets = emrTargetOptions(config);
  const offeredForms = forms.filter((f) => f.enabled !== false);
  const editorOn = admin && editor.state === "ok";
  const draftsOnly = editor.list.filter((f) => f.source === "draft_only" || (!f.head && f.draft));

  const closeEditor = () => {
    setEditId(null);
    load();
  };

  const fail = (e, fallback) => {
    const info = errInfo(e);
    if (info.status === 403) setMessage({ tone: "error", text: "Admins only" });
    else if (info.status === 409) setMessage({ tone: "warn", text: "Settings were changed elsewhere. Reloaded the latest; check and save again." });
    else setMessage({ tone: "error", text: info.message || fallback });
  };

  const discardNew = async (d) => {
    setSaving(`discard-${d.form_id}`);
    setMessage(null);
    try {
      await editorApi.discardDraft(d.form_id, d.draft ? d.draft.revision : undefined);
      await loadEditor();
      setMessage({ tone: "ok", text: `Draft “${titleOf(d.title)}” discarded.` });
    } catch (e) {
      fail(e, "Couldn't discard the draft.");
    } finally {
      setSaving("");
    }
  };

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const setEmr = (k, v) => setDraft((d) => ({ ...d, emr: { ...(d.emr || {}), [k]: v } }));
  const toggleChannel = (c, on) => setDraft((d) => {
    const cur = Array.isArray(d.channels) ? d.channels : [];
    const next = on ? CHANNELS.filter((x) => x === c || cur.includes(x)) : cur.filter((x) => x !== c);
    return { ...d, channels: next };
  });

  const savePractice = async () => {
    if (!dirty || nameErr || urlErr) return;
    setSaving("practice");
    setMessage(null);
    const body = { ...changes };
    if (original.version !== undefined) body.version = original.version;
    try {
      const practice = await formwizApi.saveConfig(body);
      clearConfigCache();
      const nextPractice = practice && typeof practice === "object" ? practice : { ...original, ...changes };
      setConfig((c) => ({ ...c, practice: nextPractice }));
      setDraft({ ...nextPractice, emr: { ...(nextPractice.emr || {}) } });
      setMessage({ tone: "ok", text: "Settings saved." });
    } catch (e) {
      fail(e, "Couldn't save the settings.");
      if (errInfo(e).status === 409) load();
    } finally {
      setSaving("");
    }
  };

  const formSettings = (id) => ((config && config.forms) || {})[id] || {};
  const saveForm = async (form, patch) => {
    const cur = formSettings(form.id);
    const base = {
      enabled: cur.enabled !== undefined ? cur.enabled : form.enabled !== false,
      auto_post: cur.auto_post !== undefined ? cur.auto_post : !!form.auto_post,
      emr: cur.emr || form.emr || CLINICAL,
    };
    if (cur.internal_note) base.internal_note = cur.internal_note;
    const body = { ...base, ...patch };
    setRowBusy(form.id);
    setMessage(null);
    try {
      const eff = await formwizApi.saveFormConfig(form.id, body);
      const next = eff && typeof eff === "object" ? eff : body;
      clearConfigCache();
      setConfig((c) => ({ ...c, forms: { ...((c && c.forms) || {}), [form.id]: next } }));
      setForms((fs) => fs.map((f) => (f.id === form.id ? { ...f, enabled: next.enabled, auto_post: next.auto_post, emr: next.emr || f.emr } : f)));
    } catch (e) {
      fail(e, "Couldn't save that form's settings.");
    } finally {
      setRowBusy("");
    }
  };

  const savePacket = async (body) => {
    setSaving("packet");
    setMessage(null);
    try {
      if (editing === "new") await formwizApi.createPacket(body);
      else await formwizApi.updatePacket(editing.id, body);
      setEditing(null);
      setPackets(await formwizApi.packets(true).catch(() => packets));
      setMessage({ tone: "ok", text: "Packet saved." });
    } catch (e) {
      fail(e, "Couldn't save the packet.");
    } finally {
      setSaving("");
    }
  };

  const removePacket = async (p) => {
    setSaving(`del-${p.id}`);
    setMessage(null);
    try {
      await formwizApi.deletePacket(p.id);
      setPackets((list) => list.map((x) => (x.id === p.id ? { ...x, active: false } : x)));
      setMessage({ tone: "ok", text: `Packet “${p.name}” turned off.` });
    } catch (e) {
      fail(e, "Couldn't remove the packet.");
    } finally {
      setSaving("");
    }
  };

  if (phase === "loading") return <div className="fws fws-page"><p className="fws-muted" style={{ padding: 24 }}>Loading…</p></div>;
  if (phase === "unavailable") {
    return (
      <div className="fws fws-page">
        <div className="fws-page-head"><h1>Form settings</h1></div>
        <div className="fws-alert fws-alert-info">Form Wizard isn't available for this practice yet.</div>
      </div>
    );
  }
  if (phase === "error") {
    return (
      <div className="fws fws-page">
        <div className="fws-page-head"><h1>Form settings</h1></div>
        <div className="fws-alert fws-alert-error">Couldn't load the settings. <button type="button" className="fws-link" onClick={load}>Try again</button></div>
      </div>
    );
  }

  const sms = (config && config.sms_preview) || {};
  return (
    <div className="fws fws-page fws-stack" data-testid="form-settings">
      <div className="fws-page-head">
        <h1>Form settings</h1>
        <span className="fws-spacer" />
        {!admin && <span className="fws-chip fws-tone-warn">Admins only: read-only</span>}
      </div>
      {message && (
        <div className={`fws-alert fws-alert-${message.tone}`} role={message.tone === "error" ? "alert" : "status"}>{message.text}</div>
      )}

      <section className="fws-section fws-stack" aria-labelledby="fws-general">
        <h2 id="fws-general" className="fws-section-title">General</h2>
        <Switch label="Form Wizard on for this practice" checked={draft.enabled !== false} onChange={(v) => set("enabled", v)} disabled={!admin} testid="set-enabled" />
        <div className="fws-settings-grid">
          <div className="fws-field">
            <label className="fws-label" htmlFor="fws-deflang">Default language</label>
            <select id="fws-deflang" className="fws-select" value={draft.default_language || "en"} disabled={!admin} onChange={(e) => set("default_language", e.target.value)}>
              <option value="en">English</option>
              <option value="es">Español</option>
            </select>
          </div>
          <div className="fws-field">
            <label className="fws-label" htmlFor="fws-ttl">Link expires after (days)</label>
            <input id="fws-ttl" type="number" min={1} max={30} className="fws-input" value={draft.link_ttl_days ?? 7} disabled={!admin}
              onChange={(e) => set("link_ttl_days", Math.max(1, Math.min(30, Number(e.target.value) || 1)))} />
          </div>
          <div className="fws-field">
            <label className="fws-label" htmlFor="fws-kiosk">Tablet session (hours)</label>
            <input id="fws-kiosk" type="number" min={1} max={12} className="fws-input" value={draft.kiosk_ttl_hours ?? 4} disabled={!admin}
              onChange={(e) => set("kiosk_ttl_hours", Math.max(1, Math.min(12, Number(e.target.value) || 1)))} />
          </div>
          <div className="fws-field">
            <label className="fws-label" htmlFor="fws-maxsends">Texts/emails per patient per day</label>
            <input id="fws-maxsends" type="number" min={1} max={50} className="fws-input" value={draft.max_sends_per_patient_day ?? 5} disabled={!admin}
              onChange={(e) => set("max_sends_per_patient_day", Math.max(1, Number(e.target.value) || 1))} />
          </div>
        </div>
        <div>
          <div className="fws-label" style={{ marginBottom: 4 }}>Ways to send</div>
          <div className="fws-row" role="group" aria-label="Ways to send">
            {/* Patient email is off for practices whose outbound guard blocks it (7825):
                the server refuses "email" there too (email_blocked). */}
            {CHANNELS.filter((c) => c !== "email" || practiceFeatures().patientEmail).map((c) => (
              <label key={c} className="fws-check">
                <input type="checkbox" checked={(draft.channels || []).includes(c)} disabled={!admin} onChange={(e) => toggleChannel(c, e.target.checked)} />
                {CHANNEL_SHORT[c]}
              </label>
            ))}
          </div>
        </div>
        <div className="fws-settings-grid">
          <div className="fws-field">
            <label className="fws-label" htmlFor="fws-dname">Practice name shown to patients</label>
            <input id="fws-dname" className="fws-input" maxLength={60} value={draft.practice_display_name || ""} disabled={!admin}
              aria-invalid={!!nameErr} onChange={(e) => set("practice_display_name", e.target.value)} />
            {nameErr && <span className="fws-err">{nameErr}</span>}
          </div>
          <div className="fws-field">
            <label className="fws-label" htmlFor="fws-phone">Practice phone (on patient error pages)</label>
            <input id="fws-phone" className="fws-input" value={draft.practice_phone || ""} disabled={!admin} onChange={(e) => set("practice_phone", e.target.value)} />
          </div>
          <div className="fws-field">
            <label className="fws-label" htmlFor="fws-base">Public link address</label>
            <input id="fws-base" className="fws-input" value={draft.public_base_url || ""} disabled={!admin} placeholder="https://…"
              aria-invalid={!!urlErr} onChange={(e) => set("public_base_url", e.target.value)} />
            {urlErr ? <span className="fws-err">{urlErr}</span> : <span className="fws-help">Links look like {String(draft.public_base_url || "https://…").replace(/\/+$/, "")}/f/#…</span>}
          </div>
        </div>
        {(sms.en || sms.es) && (
          <div className="fws-stack">
            <div className="fws-label">Text message (fixed wording)</div>
            {sms.en && <div className="fws-sms-preview" data-testid="sms-preview-en">{sms.en}</div>}
            {sms.es && <div className="fws-sms-preview" data-testid="sms-preview-es">{sms.es}</div>}
          </div>
        )}
      </section>

      <section className="fws-section fws-stack" aria-labelledby="fws-emr">
        <h2 id="fws-emr" className="fws-section-title">EMR filing</h2>
        <Switch label="Send to EMR on" checked={(draft.emr || {}).enabled !== false} onChange={(v) => setEmr("enabled", v)} disabled={!admin} testid="set-emr-enabled" />
        <Switch label="Close the EMR document when it was reviewed first" checked={!!(draft.emr || {}).autoclose_after_review} onChange={(v) => setEmr("autoclose_after_review", v)} disabled={!admin} />
        <Switch label="Close auto-posted EMR documents (not reviewed yet)" checked={!!(draft.emr || {}).autoclose_auto_post} onChange={(v) => setEmr("autoclose_auto_post", v)} disabled={!admin} />
        <div className="fws-field" style={{ maxWidth: 320 }}>
          <label className="fws-label" htmlFor="fws-dept">Default department</label>
          <select id="fws-dept" className="fws-select" value={String((draft.emr || {}).default_departmentid ?? 1)} disabled={!admin}
            onChange={(e) => setEmr("default_departmentid", Number(e.target.value))}>
            {practiceDepartments().map((d) => <option key={d.id} value={String(d.id)}>{d.name} ({d.id})</option>)}
            {!practiceDepartments().some((d) => String(d.id) === String((draft.emr || {}).default_departmentid ?? 1)) && (
              <option value={String((draft.emr || {}).default_departmentid)}>{(draft.emr || {}).default_departmentid}</option>
            )}
          </select>
          <span className="fws-help">Used when the visit's department isn't known.</span>
        </div>
      </section>

      {admin && (
        <div className="fws-row">
          <button type="button" className="fws-btn fws-btn-primary" disabled={!dirty || !!nameErr || !!urlErr || saving === "practice"} onClick={savePractice}>
            {saving === "practice" ? "Saving…" : "Save settings"}
          </button>
          {dirty && (
            <button type="button" className="fws-btn fws-btn-ghost" onClick={() => setDraft({ ...original, emr: { ...(original.emr || {}) } })}>Discard changes</button>
          )}
        </div>
      )}

      <section className="fws-section" aria-labelledby="fws-forms">
        <div className="fws-row" style={{ marginBottom: 8 }}>
          <h2 id="fws-forms" className="fws-section-title" style={{ margin: 0 }}>Forms</h2>
          <span className="fws-spacer" />
          {editorOn && editor.libraryCount > 0 && (
            <button type="button" className="fws-btn fws-btn-sm" data-testid="duplicate-standard" onClick={() => setDupSource("library")}>Duplicate a standard form…</button>
          )}
          {editorOn && <button type="button" className="fws-btn fws-btn-sm fws-btn-primary" data-testid="new-form" onClick={() => setNewOpen(true)}>New form</button>}
        </div>
        {admin && editor.state === "not_installed" && (
          <p className="fws-muted fws-small" data-testid="editor-not-installed" style={{ marginBottom: 8 }}>The form editor isn't installed for this practice yet.</p>
        )}
        {editorOn && draftsOnly.length > 0 && (
          <div className="fws-stack" style={{ marginBottom: 12 }} data-testid="drafts-list">
            <div className="fws-label">Drafts (not published)</div>
            {draftsOnly.map((d) => (
              <div key={d.form_id} className="fws-req" data-testid={`draft-${d.form_id}`}>
                <div className="fws-req-head">
                  <span className="fws-strong">{titleOf(d.title)}</span>
                  <span className="fws-chip fws-tone-warn">Draft</span>
                  <span className="fws-small fws-muted">{d.form_id}</span>
                  <span className="fws-spacer" />
                  <button type="button" className="fws-btn fws-btn-sm" onClick={() => setEditId(d.form_id)}>Continue</button>
                  <button type="button" className="fws-btn fws-btn-sm fws-btn-danger" disabled={saving === `discard-${d.form_id}`} onClick={() => discardNew(d)}>Discard</button>
                </div>
                {d.draft && d.draft.updated_by && <div className="fws-req-meta">Last edited by {d.draft.updated_by}</div>}
              </div>
            ))}
          </div>
        )}
        <div className="fws-table-wrap">
          <table className="fws-table" style={{ minWidth: editorOn ? 1080 : 760 }}>
            <thead>
              <tr><th>Form</th><th>Category</th><th>{editorOn ? "Source / version" : "Version"}</th><th>Offered</th><th>Auto-post to EMR</th><th>EMR document</th><th /></tr>
            </thead>
            <tbody>
              {forms.map((f) => {
                const cur = formSettings(f.id);
                const enabled = cur.enabled !== undefined ? cur.enabled : f.enabled !== false;
                // Signed legal records are never auto-posted from the patient's PDF (the
                // server skips them): staff send them from the Form Inbox, filed from the
                // stored answers.
                const legal = LEGAL_RECORD_CATEGORIES.includes(f.category);
                const autoPost = !legal && (cur.auto_post !== undefined ? cur.auto_post : !!f.auto_post);
                const emr = cur.emr || f.emr || CLINICAL;
                const opts = targets.some((t) => targetKey(t) === targetKey(emr)) ? targets : [...targets, { ...emr, label: emr.documentsubclass }];
                return (
                  <tr key={f.id} style={{ cursor: "default" }} data-testid={`form-row-${f.id}`}>
                    <td>
                      <div className="fws-strong">{titleOf(f.title)}</div>
                      <div className="fws-small fws-muted">{f.id}</div>
                    </td>
                    <td>{CATEGORY_LABEL[f.category] || f.category || "—"}</td>
                    <td data-testid={`version-${f.id}`}>
                      {editorOn && editor.forms.get(f.id) ? (
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 4 }}>
                          <span>{editor.forms.get(f.id).source === "practice" ? "Practice" : "Standard"} v{f.version}</span>
                          {editor.forms.get(f.id).upstream_update && <span className="fws-chip fws-tone-info">Update available</span>}
                          {editor.forms.get(f.id).draft && <span className="fws-chip fws-tone-warn">Draft</span>}
                          {editor.forms.get(f.id).lock && <span className="fws-chip fws-tone-muted" title="Scoring locked; wording can be edited">{editor.forms.get(f.id).lock.instrument}</span>}
                        </div>
                      ) : `v${f.version}`}
                    </td>
                    <td>
                      <Switch label={<span className="fws-sr">Offer {titleOf(f.title)}</span>} checked={enabled} disabled={!admin || rowBusy === f.id}
                        onChange={(v) => saveForm(f, { enabled: v })} testid={`enable-${f.id}`} />
                    </td>
                    <td>
                      <Switch label={<span className="fws-sr">Auto-post {titleOf(f.title)}</span>} checked={autoPost} disabled={!admin || rowBusy === f.id || legal}
                        onChange={(v) => saveForm(f, { auto_post: v })} testid={`autopost-${f.id}`} />
                      {legal && <div className="fws-small fws-muted" data-testid={`autopost-note-${f.id}`}>Signed form: send from the Form Inbox</div>}
                    </td>
                    <td>
                      <select className="fws-select" aria-label={`EMR document for ${titleOf(f.title)}`} value={targetKey(emr)} disabled={!admin || rowBusy === f.id}
                        onChange={(e) => {
                          const t = opts.find((o) => targetKey(o) === e.target.value);
                          if (t) saveForm(f, { emr: { endpoint: t.endpoint, documentsubclass: t.documentsubclass } });
                        }}>
                        {opts.map((t) => <option key={targetKey(t)} value={targetKey(t)}>{t.label}</option>)}
                      </select>
                    </td>
                    <td>
                      <div className="fws-row" style={{ flexWrap: "nowrap" }}>
                        <button type="button" className="fws-btn fws-btn-ghost fws-btn-sm" onClick={() => setPreview(f)}>Preview</button>
                        {editorOn && (
                          <>
                            <button type="button" className="fws-btn fws-btn-sm" data-testid={`edit-${f.id}`} onClick={() => setEditId(f.id)}>Edit</button>
                            <button type="button" className="fws-btn fws-btn-ghost fws-btn-sm" data-testid={`duplicate-${f.id}`} onClick={() => setDupSource({ form_id: f.id, version: f.version })}>Duplicate</button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {forms.length === 0 && <tr><td colSpan={7} className="fws-empty">No forms published for this practice yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>

      <section className="fws-section fws-stack" aria-labelledby="fws-packets">
        <div className="fws-row">
          <h2 id="fws-packets" className="fws-section-title" style={{ margin: 0 }}>Packets</h2>
          <span className="fws-spacer" />
          {admin && !editing && <button type="button" className="fws-btn fws-btn-sm fws-btn-primary" onClick={() => setEditing("new")}>New packet</button>}
        </div>
        {editing && (
          <PacketEditor
            key={editing === "new" ? "new" : editing.id}
            packet={editing === "new" ? null : editing}
            forms={offeredForms}
            busy={saving === "packet"}
            onSave={savePacket}
            onCancel={() => setEditing(null)}
          />
        )}
        {packets.length === 0 && !editing && <p className="fws-muted fws-small">No packets yet. A packet sends several forms with one click.</p>}
        {packets.map((p) => (
          <div key={p.id} className="fws-req" data-testid={`packet-${p.id}`}>
            <div className="fws-req-head">
              <span className="fws-strong">{p.name}</span>
              {p.active === false && <span className="fws-chip fws-tone-muted">Off</span>}
              {p.default_language && <span className="fws-chip fws-tone-muted">{p.default_language.toUpperCase()}</span>}
              <span className="fws-spacer" />
              {admin && p.active !== false && (
                <>
                  <button type="button" className="fws-btn fws-btn-sm" onClick={() => setEditing(p)}>Edit</button>
                  <button type="button" className="fws-btn fws-btn-sm fws-btn-danger" disabled={saving === `del-${p.id}`} onClick={() => removePacket(p)}>Turn off</button>
                </>
              )}
            </div>
            {p.description && <div className="fws-req-meta">{p.description}</div>}
            <div className="fws-req-meta">
              {(p.form_ids || []).map((id) => {
                const f = forms.find((x) => x.id === id);
                return f ? titleOf(f.title) : id;
              }).join(" → ")}
            </div>
          </div>
        ))}
      </section>

      {practiceFeatures().staffForms && <React.Suspense fallback={null}><InternalFormsSettings config={config} /></React.Suspense>}

      <FormPreviewDialog form={preview} onClose={() => setPreview(null)} prefill={{ practice: { name: draft.practice_display_name || "" } }}
        practiceName={draft.practice_display_name || ""} initialLang={draft.default_language === "es" ? "es" : "en"} />

      {editorOn && (
        <Suspense fallback={null}>
          {newOpen && <NewFormDialog open onClose={() => setNewOpen(false)} onCreated={(id) => { setNewOpen(false); setEditId(id); }} />}
          {dupSource && (
            <DuplicateDialog open source={dupSource === "library" ? null : dupSource} initialFrom={dupSource === "library" ? "library" : "mine"}
              onClose={() => setDupSource(null)} onCreated={(id) => { setDupSource(null); setEditId(id); }} />
          )}
        </Suspense>
      )}
      {editId && editorOn && (
        <Dialog open fullScreen onClose={closeEditor} disableEscapeKeyDown aria-label="Form editor">
          <Suspense fallback={<div className="fws" style={{ padding: 24 }}><p className="fws-muted">Loading the editor…</p></div>}>
            <FormEditor key={editId} formId={editId} onClose={closeEditor} onOpenForm={(id) => setEditId(id)} practiceName={draft.practice_display_name || ""} />
          </Suspense>
        </Dialog>
      )}
    </div>
  );
}

export default function FormSettingsPage() {
  if (!practiceFeatures().formWizard) return <div className="fws fws-page"><p style={{ padding: 24 }}>Page not available</p></div>;
  return (
    <SideNavLayout>
      <EditParamHost>
        {(editId, setEditId) => <FormSettings editId={editId} setEditId={setEditId} />}
      </EditParamHost>
    </SideNavLayout>
  );
}
